import { createFileRoute, Link } from "@tanstack/react-router";
import { Topbar } from "@/components/calv/Topbar";
import { Footer } from "@/components/calv/Footer";
import { Hero } from "@/components/calv/Hero";
import { Section } from "@/components/calv/Section";
import { ProcessFlow } from "@/components/calv/ProcessFlow";
import { Capabilities } from "@/components/calv/Capabilities";
import { RelatedChannels } from "@/components/calv/RelatedChannels";
import { CTABlock } from "@/components/calv/CTABlock";
import { Callout } from "@/components/calv/Callout";
import { RevealOnScroll } from "@/components/calv/RevealOnScroll";
import { jsonLdScript, serviceSchema, breadcrumbSchema, pageHead } from "@/lib/seo";

export const Route = createFileRoute("/en/services/performance-marketing/tiktok-ads")({
  head: () => {
    const h = pageHead("pmTiktokAds", "en");
    return {
      meta: h.meta,
      links: h.links,
      scripts: [
        jsonLdScript(serviceSchema({
          name: "TikTok Ads Agency",
          description: "Spark Ads, creator sourcing, top-view strategies — managed for economic contribution, not vanity views.",
          url: "/en/services/performance-marketing/tiktok-ads",
        })),
        jsonLdScript(breadcrumbSchema([
          { name: "Home", path: "/en" },
          { name: "Services", path: "/en/services" },
          { name: "Performance Marketing", path: "/en/services/performance-marketing" },
          { name: "TikTok Ads", path: "/en/services/performance-marketing/tiktok-ads" },
        ])),
      ],
    };
  },
  component: TikTokAdsEN,
});

const steps = [
  { num: "01", title: "Analysis & Creator Market Check (3–5 days)", body: "Account audit (if TikTok activity already exists), brand tonality check for TikTok suitability, creator research in relevant topic field, competitive analysis. Output: concrete hypotheses plus realistic expectation management. Which creator profiles fit, which don't." },
  { num: "02", title: "Setup, Tracking & First Spark Ad Pipeline (1–2 weeks)", body: "Hardening TikTok Pixel plus Events API. Aligning account structure to economic logic. First creator contracts and Spark Ad code handovers. In parallel, first picazzoo variations for asset tests if creator content isn't sufficient yet. Target: productive first campaigns by end of week two." },
  { num: "03", title: "Creator Iteration & Spark Ad Scaling (from week 2–3)", body: "Weekly iteration cadence on creator performance, format variation, audience match. Spark Ads as main format plus In-Feed Ads as scaling layer. Clear learning cycles instead of random testing." },
  { num: "04", title: "Scaling & Ongoing Operations (continuous)", body: "Budget scaling in working creator-audience combinations. Building a stable creator pool for continuous asset supply. Monthly review on economic metrics, not view count or engagement rate." },
];

const capabilities = [
  { name: "Spark Ads as Main Format", detail: "Native authenticity · performance scaling" },
  { name: "Creator Sourcing & Contracts", detail: "Micro · nano · stable pool" },
  { name: "In-Feed Ads & Top View", detail: "Scaling layers beyond Spark" },
  { name: "TikTok Pixel & Events API", detail: "Server-side · match quality" },
  { name: "Asset Production with picazzoo", detail: "Brand-true · TikTok format" },
  { name: "Account Operations with GonzalAds", detail: "Bulk · asset management" },
  { name: "Channel-Specific Funnel", detail: "Different logic than Meta" },
  { name: "Reporting & Management", detail: "Economic KPIs, not views" },
];

const audience = [
  { tag: "Primary", title: "B2C E-commerce with visual products", body: "TikTok works particularly well for products that can be visually experienced — fashion, cosmetics, pet, home, sports, food. Sensible from monthly budgets around €3,000, fully scalable. Works for established brands and DTC newcomers alike." },
  { tag: "Secondary", title: "DTC brands in build-up", body: "TikTok is often the most economical scaling channel for young DTC brands — lower CPCs than Meta, higher organic reach leverage via Spark Ads. Prerequisite: strong brand story and visual communication capability." },
  { tag: "Tertiary", title: "Experience services", body: "TikTok works for travel, events, hospitality, wellness — where experience can be visually communicated. For classic services without visual leverage, TikTok is rarely the right channel." },
  { tag: "Disqualification", title: "When we're not the right partner", body: "For monthly budgets under €2,000. When the expectation is “viral hits” — we do performance marketing, not influencer management. For classic B2B or pure service businesses." },
];

const realistic = [
  "CPC advantage of 30–50% over Meta is realistic in most DACH setups — TikTok still has significantly lower auction pressure in B2C than Meta.",
  "Spark Ad performance advantage of 40–80% over In-Feed Ads (same audience, same spend) is a typical range — native creator authenticity beats classic asset optics almost always.",
  "ROAS improvement of 30–60% over unstructured influencer marketing is realistic when previously working with pure reach contracts without Spark Ad scaling.",
  "Scaling leverage of 3–8x at the same ROAS is possible when the creator pool is stable and the tracking setup works reliably.",
];

const value = [
  ["Performance management instead of view vanity.", "We report on economic contribution, not view count or engagement rate."],
  ["Stable creator pool as asset.", "Building a recurring creator network instead of one-off influencer deals."],
  ["Spark Ads as systematic lever.", "Native authenticity with repeatable performance scaling."],
  ["picazzoo asset backup for scaling.", "When creator content isn't enough: AI-driven asset production in brand voice."],
  ["Operational management by experienced hand.", "In most mandates directly by the managing director."],
  ["Compensation model without budget coupling.", "We don't earn more when you spend more."],
];

const related = [
  { to: "/en/services/performance-marketing/meta-ads", name: "Meta Ads", sub: "Complementary B2C channel with its own funnel logic" },
  { to: "/en/services/picazzoo", name: "picazzoo", sub: "AI pipeline for scalable asset production" },
  { to: "/en/services/performance-marketing/youtube-demand-gen", name: "YouTube & Demand Gen", sub: "Extended video performance strategies beyond TikTok" },
];

const channelLinks = [
  { to: "/en/services/performance-marketing/meta-ads", name: "Advertising on Facebook & Instagram", sub: "Performance on Instagram and Facebook with creative discipline." },
  { to: "/en/services/performance-marketing/youtube-demand-gen", name: "YouTube Demand Gen", sub: "Video performance on YouTube for awareness and mid-funnel conversion." },
];

function TikTokAdsEN() {
  return (
    <>
      <Topbar />
      <main id="main">
        <Hero
          variant="short"
          number="01"
          eyebrow="Performance Marketing · TikTok Ads"
          headline={<>TikTok Ads Agency — <em>Visual Performance Without Trend Chasing,</em> with Creator Discipline.</>}
          lead="Spark Ads, creator sourcing, top-view strategies — managed for economic contribution, not vanity views. Calvarius works as a TikTok Ads agency for e-commerce brands and DTC setups that understand TikTok as a performance channel — not as an influencer playground without control."
          metaStrip={["B2C Focus", "Creator Network", "Spark Ads Discipline"]}
          breadcrumbs={[
            { label: "Home", to: "/en" },
            { label: "Services", to: "/en/services" },
            { label: "Performance Marketing", to: "/en/services/performance-marketing" },
            { label: "TikTok Ads" },
          ]}
        />

        <Section number="02" kicker="Position" headline={<>What a TikTok Ads Agency beyond the <em>influencer landscape</em> delivers.</>}>
          <RevealOnScroll>
            <div className="prose max-w-2xl text-[17px] leading-relaxed text-ink-2">
              <p className="mb-5">Anyone looking for a TikTok Ads agency beyond the influencer landscape has typically made a clear observation: TikTok has developed into a serious performance channel, but the TikTok service market is predominantly influencer-marketing-driven — lots of reach, lots of views, little economic management. Anyone running TikTok Ads as a brand awareness playground burns budget. Anyone relying on viral hopes has no strategy. But anyone combining creator sourcing, Spark Ads, and systematic performance management enters the zone where TikTok becomes the most efficient social media channel for many e-commerce setups.</p>
              <p className="mb-5">Calvarius treats TikTok like any other performance channel — with clear economic management, not trend chasing. That means: we deploy <strong className="font-semibold text-ink">Spark Ads as the main format</strong> because they combine native authenticity with performance scaling. We build a creator network not made of prominent influencers but of fitting micro- and nano-creators. We track via TikTok Pixel plus Events API with active match-quality monitoring, not in default setup.</p>
              <p>Concretely: through our tool <Link to="/en/services/picazzoo" className="underline">picazzoo</Link> we produce TikTok-appropriate asset variations in brand voice where creator content isn't enough. Through <Link to="/en/services/gonzalads" className="underline">GonzalAds</Link> we manage bulk operations faster than the native Ads Manager. And we manage with the clarity that TikTok has different funnel mechanics than Meta or Google — that requires different KPIs, not copied Meta strategies.</p>
            </div>
          </RevealOnScroll>
        </Section>

        <Section variant="tinted" number="03" kicker="Audience Fit" headline={<>When you need a TikTok Ads Agency <em>— and when you don't.</em></>}>
          <RevealOnScroll>
            <div className="grid gap-px overflow-hidden rounded-lg border border-rule bg-rule sm:grid-cols-2">
              {audience.map((a) => (
                <div key={a.title} className="bg-white p-7">
                  <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-cyan-deep">{a.tag}</div>
                  <div className="mt-3 font-display text-xl text-ink">{a.title}</div>
                  <p className="mt-3 text-sm leading-relaxed text-ink-3">{a.body}</p>
                </div>
              ))}
            </div>
          </RevealOnScroll>
        </Section>

        <Section number="04" kicker="Methodology" headline={<>How our TikTok Ads Agency <em>starts mandates.</em></>}>
          <ProcessFlow steps={steps} />
          <RevealOnScroll>
            <div className="mt-10 max-w-2xl rounded-lg border border-rule bg-bg-subtle p-7">
              <p className="eyebrow mb-3">Fast start as competitive advantage</p>
              <p className="text-[15px] leading-relaxed text-ink-2">While classic influencer agencies often need six to twelve weeks before the first Spark Ads with clean performance connection run, we are productive within two to three weeks. This is possible because we don't sell creator sourcing as talent management but set it up as a performance lever with clear economic goals.</p>
            </div>
          </RevealOnScroll>
        </Section>

        <Section variant="tinted" number="05" kicker="What we deliver" headline={<>What our TikTok Ads Agency <em>concretely delivers.</em></>}>
          <Capabilities items={capabilities} />
        </Section>

        <Section number="06" kicker="Tooling" headline={<>Tools and stack of our <em>TikTok Ads management.</em></>}>
          <RevealOnScroll>
            <div className="grid gap-6 md:grid-cols-3">
              <div className="rounded-lg border border-rule bg-white p-6">
                <p className="eyebrow mb-4">Native Platform</p>
                <ul className="space-y-2 text-sm text-ink-2">
                  <li>TikTok Ads Manager</li>
                  <li>TikTok Creative Center</li>
                  <li>TikTok Pixel + Events API</li>
                  <li>Creator Marketplace (selectively)</li>
                </ul>
              </div>
              <div className="rounded-lg border border-rule bg-white p-6">
                <p className="eyebrow mb-4">Tracking & Analytics</p>
                <ul className="space-y-2 text-sm text-ink-2">
                  <li>Server-side Events API via GTM Server</li>
                  <li>Google Analytics 4</li>
                  <li>Match Quality Score monitoring</li>
                  <li>Looker Studio dashboards</li>
                </ul>
              </div>
              <div className="rounded-lg border border-rule bg-white p-6">
                <p className="eyebrow mb-4">Calvarius Tools</p>
                <ul className="space-y-3 text-sm text-ink-2">
                  <li><Link to="/en/services/picazzoo" className="font-medium text-ink hover:text-cyan-deep">picazzoo →</Link><br/><span className="text-ink-3">AI asset production in TikTok format</span></li>
                  <li><Link to="/en/services/gonzalads" className="font-medium text-ink hover:text-cyan-deep">GonzalAds →</Link><br/><span className="text-ink-3">Account operations also for TikTok</span></li>
                </ul>
              </div>
            </div>
          </RevealOnScroll>
        </Section>

        <Section variant="tinted" number="07" kicker="What's realistic" headline={<>Realistic results of <em>TikTok Ads optimization.</em></>}
          caption="TikTok is a dynamic channel in DACH — auction conditions change with market maturity.">
          <RevealOnScroll>
            <ul className="space-y-4">
              {realistic.map((r, i) => (
                <li key={i} className="flex gap-4 rounded-lg border border-rule bg-white p-6">
                  <span className="font-mono text-xs text-cyan-deep">{String(i+1).padStart(2,"0")}</span>
                  <p className="text-[15px] leading-relaxed text-ink-2">{r}</p>
                </li>
              ))}
            </ul>
          </RevealOnScroll>
        </Section>

        <Section number="08" kicker="What you gain" headline={<>What you get from our <em>TikTok Ads Agency.</em></>}>
          <RevealOnScroll>
            <div className="grid gap-px overflow-hidden rounded-lg border border-rule bg-rule md:grid-cols-2">
              {value.map(([h, b]) => (
                <div key={h} className="bg-white p-7">
                  <div className="font-display text-lg text-ink">{h}</div>
                  <p className="mt-2 text-sm leading-relaxed text-ink-3">{b}</p>
                </div>
              ))}
            </div>
          </RevealOnScroll>
        </Section>

        <RelatedChannels locale="en" items={channelLinks} />

        <Section variant="tinted" number="09" kicker="Go deeper" headline={<>Related disciplines.</>}
          caption="These topics work in interplay.">
          <RevealOnScroll>
            <div className="grid gap-6 md:grid-cols-3">
              {related.map((r) => (
                <Link key={r.to} to={r.to} className="group flex flex-col rounded-lg border border-rule bg-white p-6 transition-all hover:-translate-y-0.5 hover:border-lime hover:shadow-[0_12px_40px_-20px_oklch(0.85_0.18_125/0.5)]">
                  <h3 className="font-display text-xl font-semibold text-ink">{r.name}</h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-ink-3">{r.sub}</p>
                  <span aria-hidden className="mt-5 font-mono text-xs uppercase tracking-wider text-ink-2 group-hover:text-lime-deep">→</span>
                </Link>
              ))}
            </div>
            <div className="mt-8">
              <Link to="/en/services/performance-marketing" className="font-mono text-xs uppercase tracking-wider text-cyan-deep hover:underline">
                ← All performance marketing disciplines
              </Link>
            </div>
          </RevealOnScroll>
        </Section>

        <Section number="10" kicker="Get concrete">
          <Callout>
            <strong>In strategic context:</strong> TikTok Ads is the performance tactic for paid reach on TikTok. Anyone viewing the platform as part of an integrated social strategy — organic content plus paid amplification as one system — will find the methodological framing under <Link to="/en/services/social-media-marketing" className="underline">Social Media Marketing →</Link>.
          </Callout>
          <CTABlock
            headline={<>TikTok Ads audit in 30 minutes — <em>free and non-binding.</em></>}
            body="In a first conversation, we clarify whether TikTok Ads makes economic sense in your B2C setup — and if so, with what priority versus Meta and Google Ads. You get an honest assessment."
            primary={{ label: "Schedule a conversation", to: "/en/contact" }}
            secondary={{ label: "Performance marketing overview", to: "/en/services/performance-marketing" }}
          />
        </Section>
      </main>
      <Footer />
    </>
  );
}
